type RsChartColors = {
  offWhite: string;
  offBlack: string;
  jet: string;
  jetLight: string;
  gridLine: string;
  green: string;
  red: string;
};

// Hover-line colors per theme. These must be read LIVE at draw time:
// react-chartjs-2 only applies inline `plugins` at chart construction and never
// re-registers them on re-render, so a color captured in the closure would never
// update when the user switches light/dark mode. The ThemeProvider keeps
// `document.documentElement[data-theme]` in sync ('' = dark, 'light' = light),
// so we read that on every draw instead.
const HOVER_LINE_DARK = '#e9ede6';
const HOVER_LINE_LIGHT = '#1e2019';

const resolveHoverLineColor = () => {
  if (typeof document === 'undefined') return HOVER_LINE_DARK;
  return document.documentElement.getAttribute('data-theme') === 'light'
    ? HOVER_LINE_LIGHT
    : HOVER_LINE_DARK;
};

export const createVerticalHoverLinePlugin = (_rs?: RsChartColors) => ({
  id: 'verticalHoverLine',
  afterDraw: (chart: any) => {
    const tooltip = chart.tooltip;
    if (!tooltip || !tooltip._active || tooltip._active.length === 0) return;
    const x = tooltip._active[0].element.x;
    const yScale = chart.scales.y;
    if (!yScale) return;
    const ctx = chart.ctx;
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(x, yScale.top);
    ctx.lineTo(x, yScale.bottom);
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 4]);
    ctx.strokeStyle = resolveHoverLineColor();
    ctx.stroke();
    ctx.restore();
  },
});

// Keep legacy export for backward compatibility
export const verticalHoverLinePlugin = createVerticalHoverLinePlugin({
  offWhite: 'rgba(243,244,246,0.45)',
  offBlack: '#1a1a1a',
  jet: '#363b33',
  jetLight: '#566151',
  gridLine: 'rgba(243,244,246,0.15)',
  green: '#80ed40',
  red: '#ef4444',
});

export const createAreaBetweenLinesPlugin = () => ({
  id: 'areaBetweenLines',
  afterDatasetsDraw: (chart: any) => {
    const ctx = chart.ctx;
    const datasets = chart.data.datasets;
    if (datasets.length < 2) return;

    const labelOf = (d: any) => String(d?.label || '').toLowerCase();
    let opIdx = datasets.findIndex((d: any) => labelOf(d).includes('operating'));
    let demIdx = datasets.findIndex((d: any) => labelOf(d).includes('demand'));

    if (opIdx === -1 || demIdx === -1) {
      const hasBarFallback = datasets.length >= 3;
      opIdx = hasBarFallback ? 1 : 0;
      demIdx = hasBarFallback ? 2 : 1;
    }

    const barIdx = datasets.findIndex((d: any) => d?.type === 'bar');

    const metaOp = chart.getDatasetMeta(opIdx);
    const metaDem = chart.getDatasetMeta(demIdx);

    if (!metaOp.data || !metaDem.data) return;

    const points0 = metaOp.data;
    const points1 = metaDem.data;
    const data0 = datasets[opIdx].data;
    const data1 = datasets[demIdx].data;

    if (barIdx >= 0) {
      const barMeta = chart.getDatasetMeta(barIdx);
      const bothLinesVisible = !metaOp.hidden && !metaDem.hidden;
      if (barMeta.elements) {
        barMeta.elements.forEach((el: any) => {
          if (el) {
            el.options.backgroundColor = bothLinesVisible ? 'rgba(132,204,22,0.4)' : 'rgba(132,204,22,0.15)';
          }
        });
      }
    }

    const redColor = 'rgba(220,20,60,0.4)';
    const greenColor = 'rgba(16,128,50,0.6)';
    const colorFor = (operatingMinusDemand: number) =>
      operatingMinusDemand > 0 ? greenColor : redColor;

    const fillTriangle = (a: any, b: any, c: any, color: string) => {
      ctx.save();
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.lineTo(c.x, c.y);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    };

    const fillQuad = (a: any, b: any, c: any, d: any, color: string) => {
      ctx.save();
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.lineTo(c.x, c.y);
      ctx.lineTo(d.x, d.y);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    };

    for (let i = 0; i < points0.length - 1; i++) {
      const p0Current = points0[i];
      const p0Next = points0[i + 1];
      const p1Current = points1[i];
      const p1Next = points1[i + 1];

      const val0Current = data0[i];
      const val0Next = data0[i + 1];
      const val1Current = data1[i];
      const val1Next = data1[i + 1];

      if (
        val0Current === null ||
        val0Next === null ||
        val1Current === null ||
        val1Next === null ||
        !p0Current ||
        !p0Next ||
        !p1Current ||
        !p1Next
      ) continue;

      const diffLeft = val0Current - val1Current;
      const diffRight = val0Next - val1Next;

      if ((diffLeft > 0 && diffRight < 0) || (diffLeft < 0 && diffRight > 0)) {
        const t = diffLeft / (diffLeft - diffRight);
        const cross = {
          x: p0Current.x + t * (p0Next.x - p0Current.x),
          y: p0Current.y + t * (p0Next.y - p0Current.y),
        };
        fillTriangle(p0Current, cross, p1Current, colorFor(diffLeft));
        fillTriangle(cross, p0Next, p1Next, colorFor(diffRight));
      } else {
        const color = colorFor(diffLeft !== 0 ? diffLeft : diffRight);
        fillQuad(p0Current, p0Next, p1Next, p1Current, color);
      }
    }
  },
});
